import { FC, useState, useEffect, useRef, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import { ModalHeader, CenteredModal, hideScrollbarStyles, Backdrop, ModalButtonContainer } from './SharedStyledComponents'
import { Text, Button } from 'ui'
import { XIcon, CuponsFileUploadIcon, CalendarClockIcon, LocationIcon, Vicon, InfoIcon, ThinBackArrow } from './icons'
import Step1 from './AddCouponModal/Step1'
import Step2 from './AddCouponModal/Step2'
import Step3 from './AddCouponModal/Step3'
import Step4 from './AddCouponModal/Step4'
import Step5 from './AddCouponModal/Step5'
import Summary from './AddCouponModal/Summary'
import Toast from './AddCouponModal/Toast'
import { Organization } from './AddCouponModal/types'
import { db, storage, Timestamp } from '../../../firebase'
import { collection, getDocs, addDoc } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, UploadMetadata } from 'firebase/storage'
import { generateBlurhash } from '../../../utils/blurhash'
import { PublishBottomBarConfig } from './BottomNavMobile'
import { useCoupons } from '../../../store/CouponsContext'
import { useCouponsAppData } from '../../../store/CouponsAppDataContext'
import { useCouponSettings, toLocalMidnight, lastDayToEndExclusive, daysInclusive, earliestStartDate } from '../../../store/couponSettings'
import { useDistributionLists } from '../../../store/distributionLists'


const BottomButtons = styled(ModalButtonContainer)`
border-top: 1px solid var(--Neutral-Borders-Default, #D1D1D1);
border-radius: 0 0 20px 20px;
@media (max-width: 1081px) {
  display: none;
}
`

const Container = styled(CenteredModal)`
  height: 760px;
  padding: 0;
  overflow: hidden;
  display: flex;
  background:#F6F6F6;
  flex-direction: column;
    width: 640px;
  @media (max-width: 1081px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    padding-top: 120px;
    transform: none;
    border-radius: 0;
    background: #F6F6F6;
    max-height: 100vh;
    z-index: 9999;
  }
`

const ModalBody = styled.div`
    padding: 24px 16px 100px 16px;
    overflow-y: auto;
    flex: 1;
    width: 100%;
    box-sizing: border-box;
    ${hideScrollbarStyles}
`

const ProgressBar = styled.div`
  width: 100%;
  height: 12px;
  background-color: #D6F3FF;
  position: relative;
  border-radius: 1000px;
`

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  background-color: #007AFF;
  position: absolute;
  top: 0;
  left: 0;
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
  border-radius: 1000px;
`

const HeaderContainer = styled.div`
    padding: 16px 16px 0 16px;
    background-color: #F6F6F6;
    width: 100%;
`

const ScrollDownMessage = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid #1EAFFF;
  background: #EDFAFF;
  margin-top: 12px;
`

const ToastWrapper = styled.div`
  position: fixed;
  bottom: 120px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10001;
  pointer-events: none;
`

const StepIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background-color: #4F87F8;
  border-radius: 1000px;
`

interface PublishCouponModalProps {
    onClose: () => void;
    categories: string[];
    onSuccess?: () => void;
    onBottomBarConfig?: (config: PublishBottomBarConfig | null) => void;
}

interface EditModeSnapshot {
    title: string;
    description: string;
    price: string;
    discountPercentage: string;
    selectedCategory: string;
    images: string[];
    startDate: Date | null;
    endDate: Date | null;
    selectedOrganizations: Set<string>;
    ctaType: 'whatsapp' | 'url';
    ctaValue: string;
    couponCode: string;
}

const PublishCouponModal: FC<PublishCouponModalProps> = ({ onClose, categories, onSuccess, onBottomBarConfig }) => {
    const { refreshLocalCoupons, discounts } = useCoupons();
    const appData = useCouponsAppData();
    const totalSteps = 4;

    // Step State
    const [currentStep, setCurrentStep] = useState(1);

    // Step 1 Data
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [selectedCategory, setSelectedCategory] = useState('')
    const [price, setPrice] = useState('')
    const [discountPercentage, setDiscountPercentage] = useState('')
    const [ctaType, setCtaType] = useState<'whatsapp' | 'url'>('whatsapp')
    const [ctaValue, setCtaValue] = useState('')
    const [couponCode, setCouponCode] = useState('')
    const [images, setImages] = useState<string[]>([])

    // Scheduling and pricing rules owned by the admin panel (הגדרות מערכת -> זמנים ומחירים).
    const { minRangeDays, pricePerDay } = useCouponSettings()

    // The only communities a coupon may be published to.
    const {
        lists: distributionLists,
        communities: distributionCommunities,
        isLoading: isLoadingCommunities,
        error: communitiesError,
    } = useDistributionLists()

    // Step 2 Data. A coupon always runs 00:00 to 00:00, so only days are picked:
    // `startDate` is the first day and `endDate` is the last day, inclusive.
    const [startDate, setStartDate] = useState<Date | null>(null)
    const [endDate, setEndDate] = useState<Date | null>(null)

    // Step 3 Data
    const [searchText, setSearchText] = useState('')
    const [selectedOrganizations, setSelectedOrganizations] = useState<Set<string>>(new Set())
    const [isSummaryExpanded, setIsSummaryExpanded] = useState(false)

    // Step 4 Data
    const [agreementDetails, setAgreementDetails] = useState<string[]>([])
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)

    // Toast state
    const [showToast, setShowToast] = useState(false)
    const [toastMessage, setToastMessage] = useState('')
    const [toastType, setToastType] = useState<'success' | 'fail'>('fail')
    const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Upload state
    const [isUploading, setIsUploading] = useState(false)

    // Edit mode state
    const [isEditMode, setIsEditMode] = useState(false)
    const [editModeSnapshot, setEditModeSnapshot] = useState<EditModeSnapshot | null>(null)

    // Modal body ref for scroll detection
    const modalBodyRef = useRef<HTMLDivElement>(null)

    // Refs for stable handler references (used by bottom bar config)
    const handleNextRef = useRef<() => void>(() => {})
    const handleBackRef = useRef<() => void>(() => {})

    // Publishable communities come only from the admin panel's distribution lists, never from
    // the user's own memberships. `allOrganizations` stays the flat deduplicated union so name
    // lookups (Summary) and totals keep working unchanged.
    const allOrganizations: Organization[] = useMemo(() => {
        return distributionCommunities.map(c => ({
            community: {
                community: String(c.id),
                name: c.name
            }
        }));
    }, [distributionCommunities]);

    // Filter categories (remove "הכל")
    const availableCategories = useMemo(() => {
        return categories.filter(cat => cat !== 'הכל');
    }, [categories]);

    // Progress bar calculation
    const progress = currentStep <= totalSteps ? (currentStep / totalSteps) * 100 : 100;
    const showSummary = currentStep === 5;
    // Step 6 is the post-send thank-you screen (Step5.tsx) — nothing left to go back to.
    const isSent = currentStep === 6;

    // Fetch app_settings on mount (agreement_details only — the price now comes from the
    // admin panel's זמנים ומחירים settings, see useCouponSettings above)
    useEffect(() => {
        const fetchAppSettings = async () => {
            try {
                const appSettingsRef = collection(db, 'app_settings');
                const appSettingsSnapshot = await getDocs(appSettingsRef);
                const appSettings: any[] = [];

                appSettingsSnapshot.forEach((doc) => {
                    appSettings.push({
                        id: doc.id,
                        ...doc.data(),
                    });
                });

                console.log('app_settings:', appSettings);

                if (appSettings.length > 0) {
                    const settings = appSettings[0];
                    if (settings.agreement_details && Array.isArray(settings.agreement_details)) {
                        const trimmedDetails = settings.agreement_details.map((detail: string) =>
                            typeof detail === 'string' ? detail.trim() : detail
                        );
                        setAgreementDetails(trimmedDetails);
                    }
                }
            } catch (error) {
                console.error('Error fetching app_settings:', error);
            }
        };

        fetchAppSettings();
    }, []);

    // Cleanup toast timeout on unmount
    useEffect(() => {
        return () => {
            if (toastTimeoutRef.current) {
                clearTimeout(toastTimeoutRef.current);
            }
        };
    }, []);

    // Toast helpers
    const showFailToast = (message: string) => {
        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
        }

        setToastType('fail');
        setToastMessage(message);
        setShowToast(true);

        toastTimeoutRef.current = setTimeout(() => {
            setShowToast(false);
            toastTimeoutRef.current = null;
        }, 3000);
    };

    const showSuccessToast = (message: string) => {
        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
        }

        setToastType('success');
        setToastMessage(message);
        setShowToast(true);

        toastTimeoutRef.current = setTimeout(() => {
            setShowToast(false);
            toastTimeoutRef.current = null;
        }, 3000);
    };

    // Validation
    const validateStep = (): { isValid: boolean; message: string } => {
        if (currentStep === 1) {
            if (!title.trim()) {
                return { isValid: false, message: 'נא למלא את שדה הכותרת' };
            }
            if (!description.trim()) {
                return { isValid: false, message: 'נא למלא את שדה התיאור' };
            }
            if (!selectedCategory) {
                return { isValid: false, message: 'נא לבחור קטגוריה' };
            }
            if (!price.trim()) {
                return { isValid: false, message: 'נא למלא את שדה מחיר המוצר' };
            }
            if (!discountPercentage.trim()) {
                return { isValid: false, message: 'נא למלא את שדה אחוז ההנחה' };
            }
            if (!ctaValue.trim()) {
                return { isValid: false, message: 'נא למלא את שדה קישור/מספר וואטסאפ' };
            }
            if (ctaType === 'whatsapp' && !/^\+?[\d\s\-()]{9,}$/.test(ctaValue.trim())) {
                return { isValid: false, message: 'מספר הטלפון אינו תקין' };
            }
            // Only URL benefits carry a redemption code; a WhatsApp benefit is
            // redeemed in the chat, so the field is not shown or required.
            if (ctaType === 'url') {
                if (!couponCode.trim()) {
                    return { isValid: false, message: 'נא למלא את שדה קוד קופון למימוש' };
                }
                if (couponCode.trim().length !== 6) {
                    return { isValid: false, message: 'קוד הקופון חייב להכיל בדיוק 6 תווים' };
                }
            }
            if (images.length === 0) {
                return { isValid: false, message: 'נא להעלות לפחות תמונה אחת' };
            }
        } else if (currentStep === 2) {
            if (!startDate) {
                return { isValid: false, message: 'נא לבחור תאריך התחלה' };
            }
            if (toLocalMidnight(startDate).getTime() < earliestStartDate().getTime()) {
                return { isValid: false, message: 'ההטבה מתחילה ב-00:00, ולכן תאריך ההתחלה המוקדם ביותר הוא מחר' };
            }
            if (!endDate) {
                return { isValid: false, message: 'נא לבחור תאריך סיום' };
            }
            if (daysInclusive(startDate, endDate) < minRangeDays) {
                return { isValid: false, message: `משך פרסום מינימלי הוא ${minRangeDays} ימים` };
            }
        } else if (currentStep === 3) {
            if (selectedOrganizations.size === 0) {
                return { isValid: false, message: 'נא לבחור לפחות ישוב אחד' };
            }
        }
        return { isValid: true, message: '' };
    };

    // Check if values have changed in edit mode
    const hasChanges = useMemo(() => {
        if (!isEditMode || !editModeSnapshot) return false;

        if (title !== editModeSnapshot.title) return true;
        if (description !== editModeSnapshot.description) return true;
        if (price !== editModeSnapshot.price) return true;
        if (discountPercentage !== editModeSnapshot.discountPercentage) return true;
        if (selectedCategory !== editModeSnapshot.selectedCategory) return true;
        if (ctaType !== editModeSnapshot.ctaType) return true;
        if (ctaValue !== editModeSnapshot.ctaValue) return true;
        if (couponCode !== editModeSnapshot.couponCode) return true;

        if (images.length !== editModeSnapshot.images.length) return true;
        for (let i = 0; i < images.length; i++) {
            if (images[i] !== editModeSnapshot.images[i]) return true;
        }

        if (startDate?.getTime() !== editModeSnapshot.startDate?.getTime()) return true;
        if (endDate?.getTime() !== editModeSnapshot.endDate?.getTime()) return true;

        if (selectedOrganizations.size !== editModeSnapshot.selectedOrganizations.size) return true;
        const orgIds = Array.from(selectedOrganizations);
        for (let i = 0; i < orgIds.length; i++) {
            if (!editModeSnapshot.selectedOrganizations.has(orgIds[i])) return true;
        }

        return false;
    }, [isEditMode, editModeSnapshot, title, description, price, discountPercentage, selectedCategory, images, startDate, endDate, selectedOrganizations, ctaType, ctaValue, couponCode]);

    // Upload image to Firebase Storage with cache-control and blurhash
    const uploadImageToStorage = async (imageUri: string, index: number): Promise<{ url: string; blurhash: string }> => {
        try {
            // For blob URLs (from file input), fetch the blob
            const response = await fetch(imageUri);
            const blob = await response.blob();

            console.log(`Uploading image ${index}: ${(blob.size / 1024 / 1024).toFixed(2)}MB`);

            // Generate blurhash before upload
            let blurhash = '';
            try {
                blurhash = await generateBlurhash(imageUri);
                console.log(`Generated blurhash for image ${index}: ${blurhash}`);
            } catch (error) {
                console.warn('Failed to generate blurhash:', error);
            }

            const timestamp = Date.now();
            const filename = `coupons/${timestamp}_${index}.jpg`;
            const storageRef = ref(storage, filename);

            // Set metadata with cache-control header (1 year)
            const metadata: UploadMetadata = {
                contentType: blob.type || 'image/jpeg',
                cacheControl: 'public, max-age=31536000',
            };

            await uploadBytes(storageRef, blob, metadata);

            const downloadURL = await getDownloadURL(storageRef);
            return { url: downloadURL, blurhash };
        } catch (error) {
            console.error('Error uploading image:', error);
            throw error;
        }
    };

    // Upload coupon and send it to the admin approval queue. Publishing is no longer paid
    // up front: the coupon is saved as 'pending' (the admin panel's 'open' tab) and the
    // Mekomi team contacts the publisher.
    const uploadCoupon = async () => {
        try {
            setIsUploading(true);

            // Step 1: Upload all images to Firebase Storage
            const uploadedImages: { url: string; blurhash: string }[] = [];
            for (let i = 0; i < images.length; i++) {
                const result = await uploadImageToStorage(images[i], i);
                uploadedImages.push(result);
            }

            // Create media array with blurhash
            const media = uploadedImages.map((img, index) => ({
                type: 'image',
                order: index,
                url: img.url,
                blurhash: img.blurhash,
                isCover: index === 0,
            }));

            // 00:00 to 00:00. `endDate` is the last day the coupon is valid, and `endAt` is
            // stored as the exclusive boundary — midnight of the day after it — which is what
            // the app's countdown and expiry checks expect.
            const startAtDate = toLocalMidnight(startDate!);
            const endAtDate = lastDayToEndExclusive(endDate!);

            // Get current user ID
            const postingUserId = appData.userId ? String(appData.userId) : undefined;

            // Convert selected organization IDs to numbers
            const communityIds = Array.from(selectedOrganizations).map(id => parseInt(id, 10));

            // Step 2: Save coupon with status 'pending' (awaiting admin approval)
            const couponData = {
                title: title,
                description: description,
                price: parseFloat(price) || 0,
                discountTag: `${discountPercentage}%`,
                media: media,
                coverUrl: uploadedImages[0]?.url || '',
                startAt: Timestamp.fromDate(startAtDate),
                endAt: Timestamp.fromDate(endAtDate),
                createdAt: Timestamp.now(),
                status: 'pending',
                type: 'local' as const,
                communities: communityIds,
                category: selectedCategory,
                ctaType: ctaType,
                ctaValue: ctaValue.trim(),
                couponCode: ctaType === 'url' ? couponCode.trim() : '',
                postingUserId: postingUserId,
            };

            await addDoc(collection(db, 'coupons'), couponData);

            refreshLocalCoupons();
            setIsUploading(false);
            setCurrentStep(6);
            return true;
        } catch (error) {
            console.error('Error publishing coupon:', error);
            setIsUploading(false);
            showFailToast('אירעה שגיאה בשליחת ההטבה. נסה שנית.');
            return false;
        }
    };

    const handleNext = async () => {
        // If in edit mode, save changes instead of going to next step
        if (isEditMode) {
            const validation = validateStep();
            if (!validation.isValid) {
                showFailToast(validation.message);
                return;
            }
            handleSaveChanges();
            return;
        }

        if (isSent) {
            handleClose();
            onSuccess?.();
            return;
        }

        const validation = validateStep();

        if (!validation.isValid) {
            showFailToast(validation.message);
            return;
        }

        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
            setHasScrolledToBottom(false);
        } else if (currentStep === totalSteps) {
            setCurrentStep(5);
        } else if (currentStep === 5) {
            await uploadCoupon();
        }
    };

    const handleBack = () => {
        if (isEditMode) {
            handleBackToSummary();
            return;
        }

        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
            setHasScrolledToBottom(false);
        }
    };

    // Update handler refs for BottomNavMobile
    handleNextRef.current = handleNext;
    handleBackRef.current = handleBack;

    // Report bottom bar config to parent for BottomNavMobile on mobile
    useEffect(() => {
        if (!onBottomBarConfig) return;

        onBottomBarConfig({
            buttonText: getButtonText(),
            subText: '',
            isDisabled: isButtonDisabled(),
            showBack: (currentStep > 1 || (isEditMode && currentStep === 1)) && !isUploading && !isSent,
            backText: isEditMode && !hasChanges ? 'לסיכום ההטבה' : 'הקודם',
            isUploading,
            onNext: () => handleNextRef.current(),
            onBack: () => handleBackRef.current(),
        });
    }, [currentStep, isEditMode, hasChanges, isUploading, hasScrolledToBottom, showSummary, isSent]);

    // Cleanup: send null on unmount
    useEffect(() => {
        return () => {
            onBottomBarConfig?.(null);
        };
    }, []);

    const handleEditStep = (step: number) => {
        setEditModeSnapshot({
            title,
            description,
            price,
            discountPercentage,
            selectedCategory,
            images: [...images],
            startDate: startDate ? new Date(startDate) : null,
            endDate: endDate ? new Date(endDate) : null,
            selectedOrganizations: new Set(selectedOrganizations),
            ctaType,
            ctaValue,
            couponCode,
        });
        setIsEditMode(true);
        setCurrentStep(step);
        setHasScrolledToBottom(false);
    };

    const handleSaveChanges = () => {
        setIsEditMode(false);
        setEditModeSnapshot(null);
        setCurrentStep(5);
        setHasScrolledToBottom(false);
        showSuccessToast('השינויים נשמרו בהצלחה');
    };

    const handleBackToSummary = () => {
        if (!hasChanges) {
            setIsEditMode(false);
            setEditModeSnapshot(null);
            setCurrentStep(5);
            setHasScrolledToBottom(false);
        } else {
            if (currentStep > 1) {
                setCurrentStep(currentStep - 1);
                setHasScrolledToBottom(false);
            }
        }
    };

    // Step 3 Helpers
    const toggleOrganization = (id: string) => {
        const newSet = new Set(selectedOrganizations);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        if (newSet.size === 0) {
            setIsSummaryExpanded(false);
        }
        setSelectedOrganizations(newSet);
    };

    // Bulk variant for the per-list select-all. Calling toggleOrganization in a loop would not
    // work — every call rebuilds the set from the same captured render state, so only the last
    // one would survive.
    const setOrganizationsSelected = (ids: string[], selected: boolean) => {
        const newSet = new Set(selectedOrganizations);
        ids.forEach(id => (selected ? newSet.add(id) : newSet.delete(id)));
        if (newSet.size === 0) {
            setIsSummaryExpanded(false);
        }
        setSelectedOrganizations(newSet);
    };

    // מחיר ליום x מספר ישובים x מספר ימים, לפי המחיר שנקבע בפאנל האדמין.
    const calculateTotalAmount = () => {
        return Math.ceil(pricePerDay * selectedOrganizations.size * calculateDays());
    };

    const calculateDays = () => {
        // Inclusive of both the first and the last day — a one-day coupon counts as 1.
        if (startDate && endDate) {
            return Math.max(1, daysInclusive(startDate, endDate));
        }
        return 1;
    };

    const formatDateRange = () => {
        if (!startDate || !endDate) return '';
        return `${startDate.toLocaleDateString('he-IL')} - ${endDate.toLocaleDateString('he-IL')}`;
    };

    // Common formatters
    const formatDate = (date: Date) => date.toLocaleDateString('he-IL');
    const formatTime = (date: Date) => date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });

    const handleClose = () => {
        setCurrentStep(1);
        setTitle('');
        setDescription('');
        setPrice('');
        setDiscountPercentage('');
        setSelectedCategory('');
        setImages([]);
        setCtaType('whatsapp');
        setCtaValue('');
        setCouponCode('');
        setStartDate(null);
        setEndDate(null);
        setSelectedOrganizations(new Set());
        setIsSummaryExpanded(false);
        setHasScrolledToBottom(false);
        setIsUploading(false);
        setIsEditMode(false);
        setEditModeSnapshot(null);
        onClose();
    };

    // Handle scroll for step 4
    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        if (currentStep === 4) {
            const target = event.target as HTMLDivElement;
            const { scrollTop, scrollHeight, clientHeight } = target;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
            setHasScrolledToBottom(isAtBottom);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return <Step1
                    title={title} setTitle={setTitle}
                    description={description} setDescription={setDescription}
                    selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory}
                    price={price} setPrice={setPrice}
                    discountPercentage={discountPercentage} setDiscountPercentage={setDiscountPercentage}
                    discounts={discounts}
                    ctaType={ctaType} setCtaType={setCtaType}
                    ctaValue={ctaValue} setCtaValue={setCtaValue}
                    couponCode={couponCode} setCouponCode={setCouponCode}
                    images={images} setImages={setImages}
                    categories={availableCategories}
                />
            case 2:
                return <Step2
                    startDate={startDate} setStartDate={setStartDate}
                    endDate={endDate} setEndDate={setEndDate}
                    minRangeDays={minRangeDays}
                />
            case 3:
                return <Step3
                    searchText={searchText} setSearchText={setSearchText}
                    selectedOrganizations={selectedOrganizations} toggleOrganization={toggleOrganization}
                    setOrganizationsSelected={setOrganizationsSelected}
                    groupedOrganizations={[]}
                    selectedCount={selectedOrganizations.size}
                    isSummaryExpanded={isSummaryExpanded} setIsSummaryExpanded={setIsSummaryExpanded}
                    calculateTotalAmount={calculateTotalAmount}
                    calculateDays={calculateDays}
                    pricePerDay={pricePerDay}
                    distributionLists={distributionLists}
                    isLoadingCommunities={isLoadingCommunities}
                    communitiesError={communitiesError}
                    formatDateRange={formatDateRange}
                    startDate={startDate} endDate={endDate}
                    allOrganizations={allOrganizations}
                />
            case 4:
                return <Step4 agreementDetails={agreementDetails} />
            case 5:
                return <Summary
                    title={title} description={description} images={images}
                    discountPercentage={discountPercentage} price={price}
                    selectedCategory={selectedCategory}
                    selectedOrganizations={selectedOrganizations}
                    organizations={allOrganizations}
                    startDate={startDate} endDate={endDate}
                    formatDate={formatDate}
                    onEditStep={handleEditStep}
                    ctaType={ctaType} ctaValue={ctaValue} couponCode={couponCode}
                />
            case 6:
                return <Step5 />
            default:
                return null;
        }
    };

    const getStepTitle = () => {
        switch (currentStep) {
            case 1: return 'פרטי הטבה';
            case 2: return 'בחירת תאריך ושעה';
            case 3: return 'בחירת קהילות';
            case 4: return 'תנאי שימוש';
            case 5: return 'סיכום ופרסום';
            default: return '';
        }
    };

    const getStepIcon = () => {
        switch (currentStep) {
            case 1:
                return <CuponsFileUploadIcon />;
            case 2:
                return <CalendarClockIcon />;
            case 3:
                return <LocationIcon />;
            case 4:
                return <Vicon fill='#4F87F8' />;
            default:
                return <CuponsFileUploadIcon />;
        }
    };

    const getButtonText = () => {
        if (isUploading) return 'שולח...';
        if (isSent) return 'סגירה';
        if (showSummary) return 'שליחה לאישור';
        if (isEditMode) return 'שמירת שינויים';
        if (currentStep === 4) return 'אישור תנאי פרסום';
        return 'המשך';
    };

    const isButtonDisabled = () => {
        if (currentStep === 4 && !hasScrolledToBottom) return true;
        if (isUploading) return true;
        if (isEditMode && !hasChanges) return true;
        return false;
    };

    return (
        <Backdrop>
            <Container>
                <HeaderContainer>
                    <ModalHeader>
                        <Text size="lg" weight="bold">פרסום הטבה</Text>
                        <div style={{ cursor: 'pointer' }} onClick={handleClose}>
                            <XIcon />
                        </div>
                    </ModalHeader>

                    {currentStep < 5 && (
                        <>
                            <ProgressBar>
                                <ProgressBarFill progress={progress} />
                            </ProgressBar>
                            <ModalHeader style={{ paddingTop: '8px', paddingBottom: '0' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {getStepIcon()}
                                    <Text size="sm" weight="bold" style={{ color: '#000' }}>
                                        {getStepTitle()}
                                    </Text>
                                </div>
                                <div>
                                    <Text size="sm" weight="bold" style={{ color: '#000' }}>
                                        0{currentStep} / 04
                                    </Text>
                                </div>
                            </ModalHeader>
                            {currentStep === 4 && !hasScrolledToBottom && (
                                <ScrollDownMessage>
                                    <InfoIcon />
                                    <Text size="sm" weight="bold" style={{ color: '#007EFF' }}>
                                        גלול מטה על מנת לאשר תנאים
                                    </Text>
                                </ScrollDownMessage>
                            )}
                        </>
                    )}
                </HeaderContainer>

                <ModalBody ref={modalBodyRef} onScroll={handleScroll}>
                    {renderStep()}
                </ModalBody>

                <BottomButtons isFixed={true}>
                    {(currentStep > 1 || (isEditMode && currentStep === 1)) && !isUploading && !isSent && (
                        <div
                            onClick={handleBack}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                cursor: 'pointer',
                                padding: '8px',
                                flex: 1
                            }}
                        >
                            <ThinBackArrow />
                            <span style={{ color: '#5D5D5D', fontSize: '20px', fontWeight: 700 }}>
                                {isEditMode && !hasChanges ? 'לסיכום ההטבה' : 'הקודם'}
                            </span>
                        </div>
                    )}
                    <Button
                        variant="contained"
                        onClick={handleNext}
                        style={{
                            opacity: isButtonDisabled() ? 0.5 : 1,
                            cursor: isButtonDisabled() ? 'not-allowed' : 'pointer',
                            width: '212px',
                            fontSize: '20px',
                            borderRadius: '8px'
                        }}
                        disabled={isButtonDisabled()}
                    >
                        <span>{getButtonText()}</span>
                    </Button>
                </BottomButtons>
            </Container>

            {showToast && (
                <ToastWrapper>
                    <Toast type={toastType} title={toastMessage} />
                </ToastWrapper>
            )}
        </Backdrop>
    )
}

export default PublishCouponModal
